import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import type {
  CreateWorkflowRequest,
  UpdateWorkflowDraftRequest,
  Workflow,
  WorkflowDeleted,
  WorkflowSummary,
} from '../../../types/backend';

// Workflow definitions (ADR-0055), authored in the Studio. Every write lands on the DRAFT;
// publishing copies it to the next immutable version, and the backend decides whether it may.
export const createWorkflowService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    listWorkflows: async (includeDrafts = true): Promise<WorkflowSummary[]> =>
      rpcCall<WorkflowSummary[]>(client, 'workflow.ListWorkflows', {
        include_drafts: includeDrafts,
      }),

    // Omitting `version` asks for the latest published one; a draft must be named explicitly,
    // so nothing renders a mutable draft as if it were runnable.
    getWorkflow: async (workflowId: string, version?: string): Promise<Workflow> =>
      rpcCall<Workflow>(client, 'workflow.GetWorkflow', {
        workflow_id: workflowId,
        ...(version ? { version } : {}),
      }),

    createWorkflow: async (request: CreateWorkflowRequest): Promise<Workflow> =>
      rpcCall(client, 'workflow.CreateWorkflow', request),

    updateDraft: async (request: UpdateWorkflowDraftRequest): Promise<Workflow> =>
      rpcCall(client, 'workflow.UpdateWorkflowDraft', request),

    publishWorkflow: async (workflowId: string): Promise<Workflow> =>
      rpcCall(client, 'workflow.PublishWorkflow', { workflow_id: workflowId }),

    deleteWorkflow: async (workflowId: string): Promise<WorkflowDeleted> =>
      rpcCall(client, 'workflow.DeleteWorkflow', { workflow_id: workflowId }),
  };
};

export const useWorkflowService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('useWorkflowService requires authentication');
    }

    return createWorkflowService(idToken);
  }, [idToken]);
};
